import {
  SHARED_ROUTES,
  MED_G_ROUTES,
  SOMMEIL_ROUTES,
  SOMMEIL_BLIGNY_ROUTES,
  MON_BILAN_PREVENTION_ROUTES,
} from '@/routes';
import { Mode } from '@/types/graph';
import type {
  PatientDataCollectionConfig,
  PatientDataCollectionKey,
} from '@aldebaran/common/util/patientDataCollection';

/**
 * StepId union type — every possible onboarding/offboarding step.
 * These map 1:1 to REAL routes in AppRoutes.tsx.
 *
 * NOTE: 'landing' is a virtual step — it has no <Route> in AppRoutes.tsx.
 * It serves as a staging target: EntryRouter navigates here after dispatching
 * state, then NavigationProvider redirects to flow[0].
 */
// These arrays are the single source of truth: the step-id types derive from
// them, STEP_ROUTES enforces exhaustive route coverage, and runtime consumers
// enumerate onboarding steps by importing the array rather than re-declaring it.
export const ONBOARDING_STEP_IDS = [
  'landing',
  // New landing pages (split from generic 'landing')
  'invitation-authentication',
  'landing-professional-select',
  'landing-practice',
  'landing-sommeil-bligny',
  'landing-etp-sommeil',
  'landing-prom-ars',
  // Med-G onboarding
  'why-prepare',
  'steps-overview',
  // Sommeil onboarding (shared between SOMMEIL and SOMMEIL_BLIGNY)
  'sommeil-intro-1',
  'sommeil-intro-2',
  // Sommeil Bligny specific
  'sommeil-bligny-recherche',
  // MBP onboarding
  'mbp-intro-1',
  'mbp-intro-2',
  'mbp-ineligible', // terminal step — flow ends here if ineligible
  // Core onboarding steps
  'patient-information',
  'patient-administrative',
  'patient-contact',
  'patient-address',
  'patient-profession',
  'self-identification',
  'consultation-time',
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEP_IDS)[number];

export const OFFBOARDING_STEP_IDS = [
  'additional-document-upload',
  'research-consent',
  'confirmation',
  'direct-confirmation',
] as const;

export type OffboardingStepId = (typeof OFFBOARDING_STEP_IDS)[number];

export type StepId = OnboardingStepId | OffboardingStepId;

/**
 * Map StepId → actual URL path.
 * EVERY entry here MUST have a corresponding <Route> in AppRoutes.tsx.
 */
export const STEP_ROUTES: Record<StepId, string> = {
  // Onboarding
  landing: SHARED_ROUTES.LANDING_PAGE, // /intro/landing-page (virtual — redirected by NavigationProvider)
  'invitation-authentication': SHARED_ROUTES.INVITATION_AUTHENTICATION, // /intro/invitation-authentication
  'landing-professional-select': SHARED_ROUTES.LANDING_PROFESSIONAL_SELECT, // /intro/landing-professional-select
  'landing-practice': SHARED_ROUTES.LANDING_PRACTICE, // /intro/landing-practice
  'landing-sommeil-bligny': SHARED_ROUTES.LANDING_SOMMEIL_BLIGNY, // /sommeil/landing-bligny
  'landing-etp-sommeil': SHARED_ROUTES.LANDING_ETP_SOMMEIL, // /sommeil/landing-etp
  'landing-prom-ars': SHARED_ROUTES.LANDING_PROM_ARS, // /prom-ars/landing
  'why-prepare': MED_G_ROUTES.WHY_PREPARE_CONSULTATION, // /intro/why-prepare-consultation
  'steps-overview': MED_G_ROUTES.QUESTIONNAIRE_STEPS_OVERVIEW, // /intro/questionnaire-steps-overview
  'sommeil-intro-1': SOMMEIL_ROUTES.INTRO_1, // /sommeil/intro1
  'sommeil-intro-2': SOMMEIL_ROUTES.INTRO_2, // /sommeil/intro2
  'sommeil-bligny-recherche': SOMMEIL_BLIGNY_ROUTES.RECHERCHE, // /sommeil-bligny/recherche
  'mbp-intro-1': MON_BILAN_PREVENTION_ROUTES.INTRO_1, // /mon-bilan-prevention/intro1
  'mbp-intro-2': MON_BILAN_PREVENTION_ROUTES.INTRO_2, // /mon-bilan-prevention/intro2
  'mbp-ineligible': MON_BILAN_PREVENTION_ROUTES.INELIGIBLE, // /mon-bilan-prevention/ineligible
  'patient-information': SHARED_ROUTES.PATIENT_INFORMATION, // /intro/patient-information
  'patient-administrative': SHARED_ROUTES.PATIENT_ADMINISTRATIVE, // /intro/patient-administrative
  'patient-contact': SHARED_ROUTES.PATIENT_CONTACT, // /intro/patient-contact
  'patient-address': SHARED_ROUTES.PATIENT_ADDRESS, // /intro/patient-address
  'patient-profession': SHARED_ROUTES.PATIENT_PROFESSION, // /intro/patient-profession
  'self-identification': SHARED_ROUTES.SELF_IDENTIFICATION_QUESTION, // /intro/self-identification-question
  'consultation-time': SHARED_ROUTES.CONSULTATION_TIME, // /intro/consultation-time
  // Offboarding
  'additional-document-upload': SHARED_ROUTES.ADDITIONAL_DOCUMENT_UPLOAD, // /additional-document-upload
  'research-consent': MED_G_ROUTES.RESEARCH_CONSENT, // /research-consent
  confirmation: MED_G_ROUTES.CONFIRMATION, // /confirmation
  'direct-confirmation': SHARED_ROUTES.DIRECT_CONFIRMATION, // /direct-confirmation
};

/** The onboarding step that collects each practice-configurable patient field. */
export const COLLECTED_FIELD_STEPS: Record<PatientDataCollectionKey, StepId> = {
  BIRTH_NAME: 'patient-information',
  BIRTH_PLACE: 'patient-administrative',
  SOCIAL_SECURITY_NUMBER: 'patient-administrative',
  PHONE: 'patient-contact',
  EMAIL: 'patient-contact',
  ADDRESS: 'patient-address',
  PROFESSION: 'patient-profession',
};

/**
 * Steps dropped when a professional fills the questionnaire in-office
 * (assisted, interlocutor = OTHER). These are the remote-onboarding /
 * patient-facing intro steps that make no sense when an assistant drives the
 * device. To exempt a future step, add it here — no transformer change needed.
 *
 * Note 1: `landing-professional-select` / `landing-practice` are intentionally
 * NOT listed — they are dropped only when a professional is already assigned
 * (handled conditionally in `skipStepsForAssistedByPro`).
 *
 * Note 2: `consultation-time` is intentionally NOT listed — office staff may
 * still enter the appointment date/time manually for a walk-in. For an assisted
 * launch it is instead auto-skipped by `skipConsultationTime` when the date is
 * already known (`consultationTimeKnown`); a walk-in with no known date keeps it.
 */
export const STEPS_TO_SKIP_WHEN_ASSISTED: StepId[] = [
  'why-prepare',
  'steps-overview',
  'sommeil-intro-1',
  'sommeil-intro-2',
  'mbp-intro-1',
  'mbp-intro-2',
  'self-identification',
];

/**
 * Flow variables — things that affect the STEP ORDER.
 *
 * Rendering variables (what shows inside a step) are NOT here.
 *
 * NOTE: `referrer` is intentionally NOT in this interface.
 * referrer=site is handled by setting variant to snp-demo at entry time.
 * The variant's patientOnboarding=SNP_DEMO and patientOffboarding=CONFIRMATION_ONLY
 * handle the entire demo flow declaratively.
 */
export interface OnboardingContext {
  // Primary flow keys (select base flow)
  /** Key into BASE_FLOWS — from questionnaireConfig.patientOnboarding */
  patientOnboarding: string;
  /** Key into OFFBOARDING_FLOWS — from questionnaireConfig.patientOffboarding */
  patientOffboarding: string;

  // Mode (affects consultation-time and self-identification)
  mode: Mode;

  // Patient state (affects patient-info and self-identification steps)
  patientDateOfBirth: string | null;
  isPatientInformationComplete: boolean;

  // Derived booleans (for transformer clarity)
  /** True when mode === 'INVITATION' */
  isInvitation: boolean;
  /** True when the patient has been selected (invitation auth completed) */
  isPatientAuthenticated: boolean;
  /** From consultation state — skips consultation-time when true */
  noAppointment: boolean;

  // Query params that affect flow order
  /** searchParams['self'] — 'true' or 'false' skips self-identification */
  selfParam: string | undefined;
  /** searchParams['goto'] — 'pie' is entry shortcut, not a flow transformation */
  gotoParam: string | undefined;

  skipProfessionalSelection: boolean;

  /** True when a professional is filling the questionnaire in-office. */
  assistedByPro: boolean;
  /** True when a professional is already selected (no doctor-select needed). */
  hasAssignedProfessional: boolean;
  /**
   * True when the consultation date/time is already known (consultation.date is
   * set, via `updateConsultationDate`). `skipConsultationTime` uses this (gated
   * on `assistedByPro`) to auto-skip the consultation-time step for a
   * consultation-linked launch. Derived from consultation.date rather than the
   * `CONSULTATION_TIME_KNOWN` graph option, which `setQuestionnaireConfig` clears
   * when it rebuilds graph.options for a non-default variant.
   */
  consultationTimeKnown: boolean;

  /**
   * Controlling professional's `patientDocumentsEnabled` master gate. When false,
   * the document upload feature is off entirely (no additional-upload page).
   */
  patientDocumentsEnabled: boolean;

  /**
   * Controlling professional's `patientDocumentAdditionalUploadsEnabled`. The
   * optional catch-all document-upload page is appended before the confirmation
   * step only when this AND `patientDocumentsEnabled` are true.
   */
  allowAdditionalUploads: boolean;

  patientDataCollectionConfig: PatientDataCollectionConfig;

  // Experiment flags (from PostHog feature flags)
  experimentFlags: Record<string, string>;
}
